/* eslint-disable camelcase */
import { nestFlatQuery } from './preload';
import { pageContext } from './page_context';

const VERTICAL_QUOTE_STATE_KEY = 'vertical.quote';
const VERTICAL_QUOTE_FORM_STATE_KEY = 'vertical.quote.form';
const VERTICAL_CALL_ME_BACK_STATE_KEY = 'vertical.callMeBack';
const VERTICAL_CALL_ME_BACK_FORM_STATE_KEY = 'vertical.callMeBack.form';
const PREFILL_TYPE = {
    HOME_CONTENTS_INSURANCE: 'home-contents-insurance',
    CAR_INSURANCE: 'car-insurance'
};

/**
 * This uses the event listener to subscribe the 'ctmca' channel
 * to retrieve the profile data loaded from the Profile API via the Customer Account micro-ui
 * The profile data will be populated to desired field for specific vertical if available.
 *
 * @param {Function} dispatch - Redux dispatch method.
 */
export const subscribeCustomerAccountEvent = (dispatch) => {
    const state = pageContext.getProps()
        .store
        .getState();
    // This flag to determine if the customer account event is subscribed
    // and to prevent multiple subscriptions
    const isCustomerAccountEventSubscribed = state
        .getIn(['page', 'isCustomerAccountEventSubscribed']);
    const experimentId = state.getIn(['page', 'experimentId']);

    // A/B testing is currently in use to feature-test the profile pre-fill ability
    // TODO: remove A/B testing on request
    if (experimentId && experimentId === 'customerAccounts' && !isCustomerAccountEventSubscribed) {
        window.addEventListener('ctmca', (e) => _onAuthStatusChange(e, dispatch), false);
    }
};

/**
 * This handler dispatch the action to set modified field values for specific vertical.
 * @param {Object} event - event object
 * @param {Function} dispatch - Redux dispatch method.
 * @private
 */
const _onAuthStatusChange = (event, dispatch) => {
    const pageState = pageContext.getProps()
        .store
        .getState();
    const vertical = pageState.getIn(['page', 'vertical']);

    const { customerAccount } = event.detail || {};
    const { isLoggedIn, profile } = customerAccount || {};

    // Populate the profile data only if the user is logged in and profile data exists.
    if (!isLoggedIn || !profile) return;

    // Pre-fill Car or H&C journey only in CA release 1
    if (vertical === PREFILL_TYPE.CAR_INSURANCE) {
        const formattedData = _formatCarJourneyPrefillData(profile);
        _prefillValues(formattedData, dispatch);
    } else if (vertical === PREFILL_TYPE.HOME_CONTENTS_INSURANCE) {
        const formattedData = _formatHCJourneyPrefillData(profile);
        _prefillValues(formattedData, dispatch);
    }

    dispatch({
        type: 'SET_PAGE_VALUE',
        identifier: 'isCustomerAccountEventSubscribed',
        value: true
    });
};

/**
 * This handler parses profile data with full identifiers into a nested object ready for merging with form state for Car journey
 * @param {Object} profileData - The profile data to fill in the fields.
 * @return {[Object]} The formatted Car prefill data schema
 * @private
 */
const _formatCarJourneyPrefillData = (profileData) => {
    const {
        email,
        firstName,
        lastName,
        contactNumbers,
        gender,
        birthDate,
        addressBook
    } = profileData || {};

    const mobile = contactNumbers && contactNumbers[0] && contactNumbers[0].value;
    const address = addressBook && addressBook[0] && _mapAddressStateKey(addressBook[0]);
    const { text: fullAddress } = address || {};
    const validGender = _getValidGender(gender);

    return [
        {
            stateGetKey: VERTICAL_QUOTE_FORM_STATE_KEY,
            stateSetKey: VERTICAL_QUOTE_STATE_KEY,
            id: 'applicant.email',
            value: email
        },
        {
            stateGetKey: VERTICAL_QUOTE_FORM_STATE_KEY,
            stateSetKey: VERTICAL_QUOTE_STATE_KEY,
            id: 'applicant.firstName',
            value: firstName
        },
        {
            stateGetKey: VERTICAL_QUOTE_FORM_STATE_KEY,
            stateSetKey: VERTICAL_QUOTE_STATE_KEY,
            id: 'applicant.lastName',
            value: lastName
        },
        {
            stateGetKey: VERTICAL_QUOTE_FORM_STATE_KEY,
            stateSetKey: VERTICAL_QUOTE_STATE_KEY,
            id: 'applicant.mobile',
            value: mobile
        },
        {
            stateGetKey: VERTICAL_CALL_ME_BACK_FORM_STATE_KEY,
            stateSetKey: VERTICAL_CALL_ME_BACK_STATE_KEY,
            id: 'mobile',
            value: mobile
        }
    ];
};

/**
 * This handler parses profile data with full identifiers into a nested object ready for merging with form state for Home & Content journey
 * @param {Object} profileData - The profile data to fill in the fields.
 * @return {[Object]} The formatted H&C prefill data schema
 * @private
 */
const _formatHCJourneyPrefillData = (profileData) => {
    const {
        email,
        firstName,
        lastName,
        contactNumbers,
        birthDate,
        addressBook
    } = profileData || {};

    const mobile = contactNumbers && contactNumbers[0] && contactNumbers[0].value;
    const address = addressBook && addressBook[0] && _mapAddressStateKey(addressBook[0]);
    const { text: fullAddress } = address || {};

    return [
        {
            stateGetKey: VERTICAL_QUOTE_FORM_STATE_KEY,
            stateSetKey: VERTICAL_QUOTE_STATE_KEY,
            id: 'contactDetails.email',
            value: email
        },
        {
            stateGetKey: VERTICAL_QUOTE_FORM_STATE_KEY,
            stateSetKey: VERTICAL_QUOTE_STATE_KEY,
            id: 'policyHolder.firstName',
            value: firstName
        },
        {
            stateGetKey: VERTICAL_QUOTE_FORM_STATE_KEY,
            stateSetKey: VERTICAL_QUOTE_STATE_KEY,
            id: 'policyHolder.lastName',
            value: lastName
        },
        {
            stateGetKey: VERTICAL_QUOTE_FORM_STATE_KEY,
            stateSetKey: VERTICAL_QUOTE_STATE_KEY,
            id: 'contactDetails.mobile',
            value: mobile
        },
        {
            stateGetKey: VERTICAL_CALL_ME_BACK_FORM_STATE_KEY,
            stateSetKey: VERTICAL_CALL_ME_BACK_STATE_KEY,
            id: 'mobile',
            value: mobile
        }
    ];
};

/**
 * This removes all null/undefined values from profile data before pre-filling the journey with it.
 * Null value or 'undefined' value violate the value type in Everest schema and
 * invalid value(s) can catastrophic break/freeze the journey.
 *
 * @param {Object} obj - The object to filter out invalid values.
 * @return {Object} The new object exclude all invalid values.
 * @private
 */
const _removeInvalidValuesFromObject = (obj) => {
    return Object.entries(obj)
        .reduce((acc, [key, val]) => {
            if (obj[key] === null || obj[key] === undefined) return acc;

            acc[key] = val;
            return acc;
        }, {});
};

/**
 * The prefill data should not override any existing value for specific identifier.
 * All prefill data should be filtered out if existing value found for specific identifier.
 *
 * @param {[Object]} prefillData - The collection of query objects
 * @returns {[Object]} - The new collection of query objects which excludes data to override existing values
 * @private
 */
const _filterOutPrefillDataIfValueExistsInSchema = (prefillData) => prefillData.filter((obj) => {
    const pageState = pageContext.getProps()
        .store
        .getState();
    const { stateGetKey, id, values } = obj;
    const fullKey = `${stateGetKey}.${id}`;

    const formValue = values ? Object.entries(values)
        .find(([key]) => pageState.getIn([...`${fullKey}.${key}`.split('.')])) : pageState.getIn([...fullKey.split('.')]);
    return !formValue;
});

/**
 * To strip out item with null/undefine value from schema
 * @param {[Object]} prefillData - A prefill schema
 * @returns {[Object]} - Sanitised scchema
 * @private
 */
const _sanitisePrefillData = (prefillData) => prefillData.filter((obj) => {
    if (obj.hasOwnProperty('value')) {
        return obj.value !== null && obj.value !== undefined;
    }

    if (obj.hasOwnProperty('values')) {
        return obj.values !== null && obj.values !== undefined;
    }

    return false;
});

/**
 * This parses valid prefill data with full identifiers into a nested object ready for merging with form state
 * @param {Object} prefillData - The profile data ready for merging with form state
 * @param {Function} dispatch - Redux dispatch method.
 * @private
 */
const _prefillValues = (prefillData, dispatch) => {
    const valuesToPrefill = _filterOutPrefillDataIfValueExistsInSchema(prefillData);
    const sanitisedPrefillData = _sanitisePrefillData(valuesToPrefill);

    for (const obj of sanitisedPrefillData) {
        const { stateSetKey, id, values, value } = obj;

        if (values) {
            const stateValues = Object.entries(values)
                .reduce((acc, [key, val]) => {
                    const fullKey = `${id}.${key}`;
                    acc[fullKey] = val;
                    return acc;
                }, {});
            dispatch({
                type: 'SET_VALUES',
                values: nestFlatQuery(stateValues),
                stateKey: stateSetKey
            });
        } else {
            dispatch({
                type: 'SET_VALUE',
                identifier: id,
                value,
                stateKey: stateSetKey
            });
        }
    }
};

const _prefillManualAddress = (dispatch, schema) => {
    const pageState = pageContext.getProps()
        .store
        .getState();
    const vertical = pageState.getIn(['page', 'vertical']);

    if (vertical === PREFILL_TYPE.CAR_INSURANCE && schema.id === 'coverDetail.overnightParking.address' && schema.values && schema.values.manualInput) {
        dispatch({
            type: 'SET_VALUE',
            identifier: 'helpers.unableToFindAddress_overnightParking',
            value: 'Y',
            stateKey: schema.stateSetKey
        });
    } else if (vertical === PREFILL_TYPE.HOME_CONTENTS_INSURANCE && schema.id === 'property.address' && schema.values && schema.values.manualInput) {
        dispatch({
            type: 'SET_VALUE',
            identifier: 'helpers.unableToFindAddress_property',
            value: 'Y',
            stateKey: schema.stateSetKey
        });
    }
};

/**
 * This maps the profile address object's item key to the journey address state key
 * @param {Object} profileAddr - Profile address object
 * @returns {Object} Formatted address object
 * @private
 */
const _mapAddressStateKey = (profileAddr) => {
    const validProfileAddr = _removeInvalidValuesFromObject(profileAddr);

    return Object.entries(validProfileAddr)
        .reduce((acc, [key, val]) => {
            let k = key;
            if (key === 'postCode') {
                k = 'postcode';
            } else if (key === 'suburbName') {
                k = 'suburb';
            } else if (key === 'houseNoSel') {
                k = 'streetNumber';
            } else if (key === 'unitSel') {
                k = 'unitNumber';
            }

            acc[k] = val;
            return acc;
        }, {});
};

const _getValidGender = (gender) => (gender && ['male', 'female'].includes(gender.toString()
    .toLowerCase()) ? gender.toUpperCase() : null);

// Exported for unit tests
export {
    _onAuthStatusChange,
    _removeInvalidValuesFromObject,
    _filterOutPrefillDataIfValueExistsInSchema,
    _formatCarJourneyPrefillData,
    _formatHCJourneyPrefillData,
    _getValidGender,
    _mapAddressStateKey,
    _prefillValues,
    _sanitisePrefillData
};
